import { Breadcrumbs, Typography, Link, Stack, TablePagination } from "@mui/material";
import { useFolhaPagamentoItensVerbaViewModel } from "../../viewModels/folha_pagamento_itens_verba_view_model";
import { BackButton } from "../../../../../common/components/back_button";
import { useMemo } from "react";
import { GenerateItemFolhaPagamentoVerbaColumnDef } from "./columns";
import { CadastroItemFolhaPagamentoVerbaModal } from "./componentes/cadastro_item_folha_pagamento_verba_modal";
import { ItemFolhaPagamentoVerba } from "../../../domain/models/item_folha_pagamento_verba";
import { ChangeHistoryModal } from "../../../../../common/modals/change_history_modal";
import { isValidId } from "../../../../../common/utils/utils";
import { PageTopBar, ThreeSlotLayout } from "../../../../../common/components/page_top_bar";
import { CustomStyledDataGrid } from "./componentes/custom_styled_data_grid";

import DynamicIcon from "../../../../../common/components/dynamic_icon";
import PrimaryButton from "../../../../../common/components/primary_button";
import RefreshButton from "../../../../../common/components/refresh_button";
import AlertError from "../../../../../common/components/alert_error";
import dayjs from "dayjs";
import BottomBar from "../../../../../common/components/bottom_bar";
import DeleteDialog from "../../../../../common/components/delete_dialog";

/**
 * FolhaPagamentoItensVerba
 */

export const FolhaPagamentoItensVerba = () => {
  const viewModel = useFolhaPagamentoItensVerbaViewModel();

  const columnsDef = useMemo(() => {
    return GenerateItemFolhaPagamentoVerbaColumnDef({
      onActionQuantidadeChange: viewModel.handleQuantidadeChange,
      onActionValorUnitarioChange: viewModel.handleValorUnitarioChange,
      onActionDelete: viewModel.handleDeleteFolhaPagamentoVerbaOpenState,
    });
  }, [
    viewModel.handleQuantidadeChange,
    viewModel.handleValorUnitarioChange,
    viewModel.handleDeleteFolhaPagamentoVerbaOpenState,
  ]);

  const CustomFooter = useMemo(() => (() => {
    return (
      <div
        className="py-2 px-4 flex flex-row justify-end items-center border-t-[1px] border-t-stone-300 dark:border-t-stone-600">
        <Typography variant="subtitle1" fontWeight="bold">
          Valor Total: {viewModel.valorTotal}
        </Typography>
      </div>
    );
  }), [
    viewModel.valorTotal,
  ]);

  return (
    <>
      <PageTopBar>
        <ThreeSlotLayout
          rightSlot={
            <div className="flex flex-row gap-2">
              <div className="w-[10rem]">
                <PrimaryButton
                  startIcon={<DynamicIcon nameIcon="Add" size={25} className="text-white" />}
                  onClick={() => viewModel.handleCadastroItemFolhaPagamentoModalOpenState(true)}>
                  Incluir Verba
                </PrimaryButton>
              </div>

              <div className="flex">
                <BackButton
                  onClick={viewModel.handleBack} />
              </div>
            </div>
          }
        />
      </PageTopBar>

      {/**
        * Page
        */}

      <div className="flex w-full gap-6 flex-col py-10 px-4 mb-16">
        <Breadcrumbs aria-label="breadcrumb" className="cursor-pointer">
          <Link underline="hover" color="inherit">
            RH
          </Link>
          <Link underline="hover" color="inherit">
            Folha de Pagamento
          </Link>
          <Typography className="text-primary-green">
            Folha de Pagamento Detalhada
          </Typography>
        </Breadcrumbs>

        <div>
          <div className="div flex justify-between items-center flex-col md:flex-row gap-6 md:gap-0">
            <div className="flex gap-2 items-center w-full">
              <h1 className="text-3xl font-medium text-black dark:text-white">Folha de Pagamento Detalhada</h1>
              <RefreshButton onClick={viewModel.handleReload} />
            </div>

            <div className="w-full justify-end flex"></div>
          </div>

          {viewModel.selectedFolhaPagamento !== null && (
            <div className="w-full flex flex-col">
              <div className="w-full mt-2 text-black dark:text-white">
                <span className="font-bold text-base">{viewModel.selectedFolhaPagamento.titulo}</span>
                <span className="mx-1 font-light text-base">-</span>
                <span className="mx-1 font-light text-base">
                  Data de Pagamento: {viewModel.selectedFolhaPagamento.dataPagamento &&
                    dayjs(viewModel.selectedFolhaPagamento.dataPagamento).format("DD/MM/YYYY")}
                </span>
              </div>
              <div className="w-full text-black dark:text-white">
                <span className="font-bold text-base">Funcionario:</span>
                <span className="mx-1 font-light text-base">{viewModel.selectedFolhaPagamento.funcionario}({viewModel.selectedFolhaPagamento.cargo})</span>
              </div>
            </div>
          )}
        </div>

        {
          viewModel.errorMessage !== undefined
            ? (
              <div className="w-full">
                <AlertError
                  messageErro={viewModel.errorMessage}
                  onTryAgain={viewModel.handleReload} />
              </div>
            ) : (
              <>
                <Stack spacing={0}>
                  <CustomStyledDataGrid
                    tableName="folha_pagamento_itensverba"
                    disableColumnMenu={false}
                    getRowId={(row: ItemFolhaPagamentoVerba) => row.idItemFolhaPagamentoVerba}
                    columns={columnsDef}
                    loading={viewModel.loading}
                    rows={viewModel.itemFolhaPagamentoVerbas}
                    rowCount={viewModel.itemFolhaPagamentoVerbas.length}
                    customFooter={CustomFooter}
                    pageSizeOptions={[10, 25, 50, 100]}
                    paginationMode="server"
                    paginationModel={{
                      page: viewModel.pagination.page,
                      pageSize: viewModel.pagination.rowsPerPage,
                    }} />

                  <TablePagination
                    component="div"
                    count={viewModel.pagination.totalItems}
                    page={viewModel.pagination.page - 1}
                    onPageChange={(_, p) => viewModel.handlePageChange(p)}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    rowsPerPage={viewModel.pagination.rowsPerPage > 0 ? viewModel.pagination.rowsPerPage : 25}
                    onRowsPerPageChange={(e) => viewModel.handleRowsPerPageChange(parseInt(e.target.value))} />

                </Stack>
              </>
            )
        }
      </div>

      <BottomBar>
        <div className="w-full h-full items-center justify-between flex p-4 gap-4">
          <div>
            {
              isValidId(viewModel.selectedItemFolhaPagamento?.idItemFolhaPagamento) &&
              <PrimaryButton
                buttonColor="yellow"
                onClick={() => viewModel.handleOpenChangeHistory(null, null)}>
                <span className="text-xs">
                  Historico de alterações
                </span>
              </PrimaryButton>
            }
          </div>

          <div className="flex flex-row gap-4">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={viewModel.handleBack}>
                Cancelar
              </h1>
            </div>
            <div className="w-[130px]">
              {viewModel.saving
                ? (
                  <PrimaryButton>
                    Salvando...
                  </PrimaryButton>
                ) : (
                  <PrimaryButton
                    onClick={viewModel.handleSubmitItemsFolhaPagamentoVerbaChange}
                    startIcon={<DynamicIcon nameIcon="CheckRounded" size={25} className="text-white" />}>
                    Salvar
                  </PrimaryButton>
                )}
            </div>
          </div>
        </div>
      </BottomBar>

      {/**
        * Modals
        */}

      <CadastroItemFolhaPagamentoVerbaModal
        open={viewModel.openCadastroItemFolhaPagamentoVerbaModal}
        saving={viewModel.saving}
        itensVerbaFuncionario={viewModel.filters.itensVerbaFuncionario ?? []}
        reactHookForm={viewModel.hookForm}
        onSubmit={viewModel.handleSubmitItemFolhaPagamentoVerba}
        onClose={() => viewModel.handleCadastroItemFolhaPagamentoModalOpenState(false)} />

      <DeleteDialog
        open={viewModel.openDeleteItemFolhaPagamentoVerbaModal}
        saving={viewModel.saving}
        onClose={() => viewModel.handleDeleteFolhaPagamentoVerbaOpenState(null, false)}
        title={`Você realmente deseja excluir este item?`}
        onDelete={viewModel.handleConfirmarDeletarItemFolhaPagamentoVerba} />

      {/**
        * Modal historico de alterações.
        */}

      <ChangeHistoryModal
        changeHistoryHandler={viewModel.changeHistory} />
    </>
  );
}